/**
 * TrackerGroupsSection - Displays trackers in the sidebar grouped by preference
 *
 * Refactored to use the generic SidebarResourceSection and GenericResourceGroup components.
 */

import React, { useCallback, useState } from "react";
import { Tracker } from "@/models/tracker";
import {
  RunTrackerOptions,
  runTrackerWithOptions,
  useUpdateTrackerGroup,
} from "@/api/tracker";
import { useResourceGroups } from "@/api/resource-group";
import { useTrackersStore } from "@/hooks";
import { useTrackerGroupBy } from "@/hooks/use-resource-group-by";
import { trackerGalleryConfig } from "@/config/resource-gallery-configs";
import { trackerSectionConfig } from "@/config/sidebar-section-configs";
import useNotif from "@/hooks/use-notif";
import SidebarResourceSection, {
  SidebarGroupWithItemsRenderProps,
} from "./sidebar-resource-section";
import GenericResourceGroup, {
  createUpdateItemGroupFn,
} from "./generic-resource-group";
import TrackerItem from "./tracker-item";
import { GroupableItem } from "./resource-group-item";
import { RunTrackerModal } from "@/components/modals/run-tracker-modal";

// =============================================================================
// Types
// =============================================================================

interface TrackerGroupsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

// Adapter type to make Tracker compatible with GroupableItem
type TrackerGroupable = Tracker & GroupableItem;

// =============================================================================
// Component
// =============================================================================

const TrackerGroupsSection: React.FC<TrackerGroupsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { addSuccess, addError } = useNotif();

  // Fetch trackers via store
  const { resources: trackers } = useTrackersStore();

  // Fetch resource groups for trackers
  const { data: resourceGroups = [] } = useResourceGroups("tracker");

  // Get groupBy preference from shared hook
  const { groupBy } = useTrackerGroupBy();

  // Mutations
  const updateTrackerGroupMutation = useUpdateTrackerGroup();

  // Run tracker modal state
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [trackerToRun, setTrackerToRun] = useState<Tracker | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // Create the updateItemGroup function for GenericResourceGroup
  const updateItemGroup = useCallback(
    async (itemId: string, groupId: string) => {
      await updateTrackerGroupMutation.mutateAsync({
        group: { id: groupId },
        uriParams: { id: itemId },
      });
    },
    [updateTrackerGroupMutation.mutateAsync]
  );

  // Handle run tracker - opens modal to configure options
  const handleRunTracker = useCallback((tracker: Tracker) => {
    setTrackerToRun(tracker);
    setRunModalOpen(true);
  }, []);

  // Handle run tracker with options (from modal)
  const handleRunTrackerConfirm = useCallback(
    async (options: RunTrackerOptions) => {
      if (!trackerToRun) return;
      setIsRunning(true);
      try {
        await runTrackerWithOptions(trackerToRun.id, options);
        addSuccess("Tracker exécuté avec succès");
        setRunModalOpen(false);
        setTrackerToRun(null);
      } catch (error) {
        addError("Erreur lors de l'exécution du tracker");
      } finally {
        setIsRunning(false);
      }
    },
    [trackerToRun, addSuccess, addError],
  );

  // Close run modal
  const handleCloseRunModal = useCallback(() => {
    setRunModalOpen(false);
    setTrackerToRun(null);
  }, []);

  // Render a single tracker item
  const renderTrackerItem = useCallback(
    (
      tracker: Tracker,
      isSelected: boolean,
      orderedIds: string[],
      multiSelectContextMenu?: (e: React.MouseEvent) => void,
      onDropToCreateGroup?: (draggedId: string, targetId: string) => void,
    ) => {
      return (
        <TrackerItem
          key={tracker.id}
          tracker={tracker}
          onRunTracker={handleRunTracker}
          isSelected={isSelected}
          orderedIds={orderedIds}
          onMultiSelectContextMenu={multiSelectContextMenu}
          onDropToCreateGroup={onDropToCreateGroup}
        />
      );
    },
    [handleRunTracker],
  );

  // Render a group using GenericResourceGroup
  const renderGroup = useCallback(
    (props: SidebarGroupWithItemsRenderProps<Tracker>) => (
      <GenericResourceGroup<TrackerGroupable>
        key={props.groupKey}
        groupKey={props.groupKey}
        items={props.items as TrackerGroupable[]}
        groupBy={props.groupBy}
        resourceType="tracker"
        renderItem={renderTrackerItem}
        getGroupIcon={trackerSectionConfig.getGroupIcon}
        resourceGroups={resourceGroups}
        allItems={trackers as TrackerGroupable[]}
        updateItemGroup={updateItemGroup}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [renderTrackerItem, resourceGroups, trackers, updateItemGroup],
  );

  // Render simple item (for ungrouped mode)
  const renderItem = useCallback(
    (props: {
      item: Tracker;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <TrackerItem
        key={props.item.id}
        tracker={props.item}
        onRunTracker={handleRunTracker}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [handleRunTracker],
  );

  return (
    <>
      <SidebarResourceSection<Tracker>
        items={trackers}
        config={trackerGalleryConfig}
        renderItem={renderItem}
        renderGroup={renderGroup}
        emptyMessage={trackerSectionConfig.emptyMessage}
        noResultsMessage={trackerSectionConfig.noResultsMessage}
        searchPlaceholder={trackerSectionConfig.searchPlaceholder}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        externalGroupBy={groupBy}
      />

      {/* Run Tracker Modal */}
      <RunTrackerModal
        open={runModalOpen}
        onClose={handleCloseRunModal}
        tracker={trackerToRun}
        onConfirm={handleRunTrackerConfirm}
        isLoading={isRunning}
      />
    </>
  );
};

TrackerGroupsSection.displayName = "TrackerGroupsSection";

export default TrackerGroupsSection;
